import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Typography
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { IProfessorAreaAssessmentAttempts } from 'interfaces/professor_area/assessments';
import { IProfessorAreaEvaluationAttemptList } from 'interfaces/professor_area/evaluations';
import { ResponseError } from 'interfaces/api-error';
import { useApiUrl, useCustomMutation } from '@refinedev/core';
import { useParams } from 'react-router-dom';

type EditScoreAttempt =
  | IProfessorAreaAssessmentAttempts
  | IProfessorAreaEvaluationAttemptList;

interface Props {
  attempt: EditScoreAttempt | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const getAttemptUserName = (attempt: EditScoreAttempt | null) => {
  if (!attempt) return undefined;
  if ('user' in attempt) return attempt.user?.name;
  return attempt.user_name;
};

const errorNotification = (error: unknown) => {
  const err = error as ResponseError;

  const code =
    err && typeof err === 'object' && 'code' in err ? err.code : undefined;

  if (code === 'classrooms_not_available_error') {
    return {
      type: 'error' as const,
      description: 'Turma não ativa',
      message:
        'Não é possível editar a nota pois a turma vinculada a essa avaliação não está mais ativa!'
    };
  }

  if (
    code === 'assessments_attempts_status_error' ||
    code === 'assignments_attempts_status_error'
  ) {
    return {
      type: 'error' as const,
      description: 'Aluno não submeteu a tentativa',
      message:
        'Não é possível lançar nota — o aluno precisa primeiro submeter a tentativa.'
    };
  }

  return {
    type: 'error' as const,
    description: 'Ops! Algo de errado',
    message: 'Não foi possível editar a nota'
  };
};

const successNotification = () => ({
  type: 'success' as const,
  description: 'Sucesso!',
  message: 'Nota alterada com sucesso'
});

export const EditScoreDrawer: React.FC<Props> = ({
  attempt,
  open,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const apiUrl = useApiUrl();
  const { classroomId, id: evaluationId } = useParams();

  const { mutateAsync: forceScoreAsync } = useCustomMutation();

  useEffect(() => {
    if (open && attempt) {
      form.setFieldsValue({
        score: attempt.score,
        justification: ''
      });
    }
  }, [open, attempt, form]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const onMutateScore = useCallback(async () => {
    if (!attempt) {
      return;
    }

    const values = {
      score: Number(form.getFieldValue('score')),
      justification: form.getFieldValue('justification')
    };

    const url = `${apiUrl}/professor_area/classrooms/${classroomId}/evaluations/${evaluationId}/attempts/${attempt.id}/force_score`;

    await forceScoreAsync({
      url,
      method: 'patch',
      values,
      errorNotification,
      successNotification
    });

    onSuccess?.();
    handleClose();
  }, [
    attempt,
    apiUrl,
    classroomId,
    evaluationId,
    forceScoreAsync,
    form,
    onSuccess,
    handleClose
  ]);

  const onSubmit = useCallback(async () => {
    try {
      setLoading(true);
      await form.validateFields();
      await onMutateScore();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [form, onMutateScore]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={500}
      destroyOnClose
      closable={false}
      title={
        <Flex vertical>
          <Typography.Title level={4} className="!mb-0">
            Lançar nota
          </Typography.Title>
          <Typography.Text type="secondary" className="font-normal">
            {getAttemptUserName(attempt)}
          </Typography.Text>
        </Flex>
      }
      extra={
        <Button
          icon={<CloseOutlined />}
          onClick={handleClose}
          disabled={loading}
        />
      }
      footer={
        <Flex justify="end" gap={8}>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="primary" loading={loading} onClick={onSubmit}>
            Salvar
          </Button>
        </Flex>
      }
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Alert
          message="A nota só pode ser lançada enquanto a turma estiver ativa."
          className="block !mb-4"
        />

        <Form.Item
          name="score"
          label="Nota"
          rules={[
            { required: true, message: 'Campo obrigatório' },
            {
              validator(_, value, callback) {
                if (value === attempt?.score) {
                  callback('A nota deve ser diferente da atual');
                }

                callback();
              }
            }
          ]}
        >
          <InputNumber max={100} min={0} />
        </Form.Item>

        <Form.Item
          name="justification"
          label="Justificativa"
          rules={[{ required: true, message: 'Campo obrigatório' }]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
